import React, { useMemo, useState } from 'react';
import { Empty } from 'antd';
import { POLICY_LEVEL_LABELS } from '../../constants/policyFilters';
import type { Attachment, Policy } from '../../types';
import { AttachmentPreviewModal } from './AttachmentPreviewModal';

export interface PolicyDetailPanelProps {
  selectedPolicyId: string | null | undefined;
  policies: Policy[];
}

function DetailRow(props: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="dash-detail-row">
      <div className="dash-detail-row__label">{props.label}</div>
      <div className="dash-detail-row__value">{props.children}</div>
    </div>
  );
}

function inferAttachmentType(path: string): Attachment['type'] {
  const lower = path.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(lower)) return 'image';
  if (/\.pdf$/i.test(lower)) return 'pdf';
  return 'doc';
}

function FileLinks(props: {
  links: string[];
  onPreview: (attachment: Attachment) => void;
}): React.ReactElement {
  if (props.links.length === 0) return <>—</>;
  return (
    <div className="dash-tag-list">
      {props.links.map((link) => {
        const filename = decodeURIComponent(link.split('/').pop() || link);
        return (
          <button
            key={link}
            type="button"
            className="dash-attachment-link"
            onClick={() =>
              props.onPreview({
                label: filename,
                path: link,
                type: inferAttachmentType(filename),
              })
            }
          >
            {filename}
          </button>
        );
      })}
    </div>
  );
}

export function PolicyDetailPanel(props: PolicyDetailPanelProps): React.ReactElement {
  const { selectedPolicyId, policies } = props;
  const [preview, setPreview] = useState<Attachment | null>(null);

  const selected = useMemo(() => {
    if (!selectedPolicyId) return undefined;
    return policies.find((policy) => policy.id === selectedPolicyId);
  }, [policies, selectedPolicyId]);

  if (!selectedPolicyId || !selected) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span className="dash-empty-text">请选择政策</span>}
          />
        </div>
      </aside>
    );
  }

  return (
    <>
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-title">政策详情 · {POLICY_LEVEL_LABELS[selected.level]}</div>
        <div className="dash-detail-body">
          <DetailRow label="政策标题">{selected.title}</DetailRow>
          <DetailRow label="政策等级">{selected.policyTier || POLICY_LEVEL_LABELS[selected.level]}</DetailRow>
          <DetailRow label="省份">{selected.province || '—'}</DetailRow>
          {selected.level === 'park' ? (
            <DetailRow label="园区名称">{selected.parkName || '—'}</DetailRow>
          ) : (
            <DetailRow label="市区名称">{selected.city || '—'}</DetailRow>
          )}
          <DetailRow label="发布时间">{selected.issuedAt || '—'}</DetailRow>
          <DetailRow label="核心目标">
            {selected.coreGoal ? <span className="dash-detail-text-block">{selected.coreGoal}</span> : '—'}
          </DetailRow>
          <DetailRow label="支持政策">
            {selected.supportPolicy ? (
              <span className="dash-detail-text-block">{selected.supportPolicy}</span>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label="申报安排">
            {selected.applicationSchedule ? (
              <span className="dash-detail-text-block">{selected.applicationSchedule}</span>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label="相关文件">
            <FileLinks links={selected.relatedFiles} onPreview={setPreview} />
          </DetailRow>
        </div>
      </aside>
      <AttachmentPreviewModal
        open={preview != null}
        attachment={preview}
        onClose={() => setPreview(null)}
      />
    </>
  );
}
